import { HoverChar } from "@ui/hover-char";
import { Panel } from "@ui/panel";
import { cn } from "@u/cn";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { useId, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Reveal } from "./Reveal";

interface FaqCardProps {
  /** Anchor id — the `/faq` directory deep-links to a question by slug. */
  id?: string;
  open: boolean;
  onToggle: () => void;
  question: ReactNode;
  answer: ReactNode;
  /** Controls between the question and the chevron (the page's copy-link). */
  actions?: ReactNode;
  /** Directory density: `/faq` lists 100+ rows, a landing section lists ~6. */
  dense?: boolean;
}

/**
 * One FAQ question card. Presentational and CONTROLLED — the caller owns `open`,
 * so the same card serves both consumers: the landing sections open each row
 * independently (`FaqAccordion` below), and the `/faq` directory keeps a single
 * row open per category and force-opens every row while a search is active.
 *
 * Abre SOLO al click. Antes abria al pasar el mouse: al recorrer la lista las
 * filas se abrian y cerraban solas y el layout se movia bajo el cursor.
 *
 * It exists because both were hand-rolled copies of the same shell and drifted:
 * the page's copy lost its shadow in the 2026-08-31 "public surfaces are flat"
 * pass and the shared one kept it, while a comment still claimed they matched.
 */
export function FaqCard({
  id,
  open,
  onToggle,
  question,
  answer,
  actions,
  dense = false,
}: FaqCardProps) {
  const reduced = useReducedMotion();
  const panelId = useId();

  return (
    <Panel
      id={id}
      surface="flat"
      pad="none"
      className={cn(
        "group overflow-hidden transition-surface",
        open
          ? "bg-primary/5 border-primary/20"
          : "hover:bg-muted/40 hover:border-primary/30",
      )}
    >
      <div className="flex items-start gap-4">
        {/* Toggle = the question itself, so its accessible name IS the question.
            `actions` is a SIBLING, never nested, to avoid button-in-button. */}
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-controls={panelId}
          className={cn(
            "flex flex-1 items-start gap-3 p-5 text-left cursor-pointer",
            actions && "pr-0",
          )}
        >
          <HoverChar
            char="_"
            className="group-hover:w-2.5 group-hover:opacity-100 font-mono text-primary font-black shrink-0 mt-0.5"
          />
          <p
            className={cn(
              "m-0 flex-1 leading-relaxed transition-colors group-hover:text-primary dark:group-hover:text-white",
              dense ? "text-sm" : "text-base font-medium",
            )}
          >
            {question}
          </p>
          {!actions && <Chevron open={open} />}
        </button>

        {actions && (
          <div className="flex items-center gap-3 shrink-0 p-5 pl-0">
            {actions}
            {/* Aria-hidden: the question button above already toggles and is the
                labelled control — this is a redundant mouse target. */}
            <button
              type="button"
              aria-hidden
              tabIndex={-1}
              onClick={onToggle}
              className="flex shrink-0 items-center justify-center cursor-pointer"
            >
              <Chevron open={open} />
            </button>
          </div>
        )}
      </div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            id={panelId}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: reduced ? 0 : 0.3, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 pt-0 ml-4 sm:ml-6">
              <div className="border-l-2 border-primary/20 pl-4 py-1">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {answer}
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Panel>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <ChevronDown
      size={16}
      strokeWidth={1.5}
      aria-hidden
      className={cn(
        "shrink-0 mt-0.5 transition-transform duration-300",
        open
          ? "rotate-180 text-primary dark:text-white"
          : "text-muted-foreground group-hover:text-foreground",
      )}
    />
  );
}

/** Landing row: owns its own open state and resolves its i18n keys. */
function FaqItem({ qKey, aKey }: { qKey: string; aKey: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <FaqCard
      open={open}
      onToggle={() => setOpen(!open)}
      question={t(qKey)}
      answer={t(aKey)}
    />
  );
}

/**
 * Shared landing FAQ accordion — identical design across the home, .360 and
 * .tap landings. Each landing supplies its own product-relevant items and wraps
 * this in its own <Section> (title/eyebrow/CTA).
 */
export function FaqAccordion({
  items,
}: {
  items: readonly { q: string; a: string }[];
}) {
  return (
    <div className="mx-auto max-w-3xl space-y-2">
      {items.map(({ q, a }, i) => (
        <Reveal key={q} delay={i * 0.05}>
          <FaqItem qKey={q} aKey={a} />
        </Reveal>
      ))}
    </div>
  );
}
